<template>
  <div>
    <Navbar />
    
    <div class="faq-container">
      <div class="faq-header">
        <h1>Frequently Asked Questions</h1>
        <div class="divider"></div>
        <p>Feel free to contact us if you have any questions about manufacturing.</p>
      </div>

      <div class="faq-card mb-4rem">
        <h2 class="section-title">General Service QA</h2>

        <div class="accordion-group">
          <div 
            v-for="(item, index) in generalFaqList" 
            :key="'general-' + index" 
            class="accordion-item"
          >
            <button
              @click="toggleGeneral(index)"
              class="accordion-trigger"
              :class="{ 'active-trigger': activeGeneralIndex === index }"
            >
              <svg
                class="arrow-icon"
                :class="{ 'rotate': activeGeneralIndex === index }"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
              <span class="question-text">{{ item.question }}</span>
            </button>

            <div v-show="activeGeneralIndex === index" class="accordion-panel">
              <div class="panel-content">
                <div v-html="item.answer"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="faq-columns-wrapper mb-4rem">
        <div class="faq-column">
          <h2 class="section-title">CNC Machining</h2>
          
          <div class="accordion-group">
            <div 
              v-for="(item, index) in cncFaqList" 
              :key="'cnc-' + index" 
              class="accordion-item"
            >
              <button
                @click="toggleCnc(index)"
                class="accordion-trigger"
                :class="{ 'active-trigger': activeCncIndex === index }"
              >
                <svg
                  class="arrow-icon"
                  :class="{ 'rotate': activeCncIndex === index }"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span class="question-text">{{ item.question }}</span>
              </button>

              <div v-show="activeCncIndex === index" class="accordion-panel">
                <div class="panel-content">
                  <div v-html="item.answer"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="faq-column">
          <h2 class="section-title">Sheet Metal Fabrication</h2>
          
          <div class="accordion-group">
            <div 
              v-for="(item, index) in sheetMetalFaqList" 
              :key="'sheet-' + index" 
              class="accordion-item"
            >
              <button
                @click="toggleSheetMetal(index)"
                class="accordion-trigger"
                :class="{ 'active-trigger': activeSheetIndex === index }"
              >
                <svg
                  class="arrow-icon"
                  :class="{ 'rotate': activeSheetIndex === index }"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span class="question-text">{{ item.question }}</span>
              </button>

              <div v-show="activeSheetIndex === index" class="accordion-panel">
                <div class="panel-content">
                  <div v-html="item.answer"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="faq-columns-wrapper mb-4rem">
        <div class="faq-column">
          <h2 class="section-title">3D Printing/Additive Manufacturing</h2>
          
          <div class="accordion-group">
            <div 
              v-for="(item, index) in printingFaqList" 
              :key="'printing-' + index" 
              class="accordion-item"
            >
              <button
                @click="togglePrinting(index)"
                class="accordion-trigger"
                :class="{ 'active-trigger': activePrintingIndex === index }"
              >
                <svg
                  class="arrow-icon"
                  :class="{ 'rotate': activePrintingIndex === index }"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span class="question-text">{{ item.question }}</span>
              </button>

              <div v-show="activePrintingIndex === index" class="accordion-panel">
                <div class="panel-content">
                  <div v-html="item.answer"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="faq-column">
          <h2 class="section-title ">Surface Finish</h2>
          
          <div class="accordion-group">
            <div 
              v-for="(item, index) in surfaceFaqList" 
              :key="'surface-' + index" 
              class="accordion-item"
            >
              <button
                @click="toggleSurface(index)"
                class="accordion-trigger"
                :class="{ 'active-trigger': activeSurfaceIndex === index }"
              >
                <svg
                  class="arrow-icon"
                  :class="{ 'rotate': activeSurfaceIndex === index }"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span class="question-text">{{ item.question }}</span>
              </button>

              <div v-show="activeSurfaceIndex === index" class="accordion-panel">
                <div class="panel-content">
                  <div v-html="item.answer"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="cta-module">
        <div class="cta-content">
          <h2>On-demand Manufacturing Service</h2>
          <p>
            XCL provides high-quality prototypes and on-demand manufacturing service with exceptional customer support and quick turnaround times since 2009. Click to tell us your project or contact us for more information.
          </p>
          <button class="btn-cta" @click="$router.push('/quote')">TELL US YOUR PROJECT</button>
        </div>
        <div class="cta-image">
          <img src="@/assets/images/about_us/13.png" alt="CNC Machining" />
        </div>
      </div>

    </div>

    <quote />
    <Footer />
  </div>
</template>

<script setup>
import { ref } from 'vue'
import Footer from './components/Footer.vue'
import Navbar from './components/Nav.vue'
import quote from './components/quote.vue'

// 独立控制折叠索引
const activeGeneralIndex = ref(null)
const activeCncIndex = ref(null)
const activeSheetIndex = ref(null)
const activePrintingIndex = ref(null) 
const activeSurfaceIndex = ref(null)  

// 切换状态的独立函数
const toggleGeneral = (index) => {
  activeGeneralIndex.value = activeGeneralIndex.value === index ? null : index
}

const toggleCnc = (index) => {
  activeCncIndex.value = activeCncIndex.value === index ? null : index
}

const toggleSheetMetal = (index) => {
  activeSheetIndex.value = activeSheetIndex.value === index ? null : index
}

const togglePrinting = (index) => {
  activePrintingIndex.value = activePrintingIndex.value === index ? null : index
}

const toggleSurface = (index) => {
  activeSurfaceIndex.value = activeSurfaceIndex.value === index ? null : index
}

// 1. General Service QA 数据
const generalFaqList = ref([
  {
    question: 'What type of design files can you accept?',
    answer: 'We accept 3D CAD files in STL, STP, STEP, OBJ IGS or IGES format and 2D drawings(jpg, png, gif, bmp, pdf) with dimensional details and specific manufacturing requirements.'
  },
  {
    question: 'How long does it take to get a pricing quote?',
    answer: 'We respond within 2 hours after your request. DFMA analytics and Princing quote will be send within 24 hours. If the quote is delayed, we will notify you as soon as possible.'
  },
  {
    question: 'Can I use my master model for making duplicate copies for vacuum casting or CNC machining?',
    answer: 'Yes, master models are used to create silicone molds for vacuum casting and can be made from any rigid solid that can withstand heat up to 100 °C. Models used for CNC machining will be 3D scanned to create a CAD/CAM program for production.'
  },
 {
    question: 'How quickly can I get my parts?',
    answer:
      'Simple parts can be completed in as fast as one week depending on complexity and quantity.'
  },
  {
    question: 'How do we protect your data and intellectual property?',
    answer:
      'We respect your intellectual property. None of your information, images, files or likenesses will be shared with any third party, and it will not be used for any advertising, marketing, or promotional use without your explicit written authorization. If you would like to sign NDA agreement, please contact our team for support.'
  },
  {
    question: 'How about the quality guarantee policy?',
    answer:
      'We follow the ISO/IATF standard and guarantee that we will rework the products or refund them if there are any quality problems. The full-dimensional report, material certification, or first article inspection are provided on request before order placement.'
  }
])

// 2. CNC Machining 数据
const cncFaqList = ref([
  {
    question: 'What is cnc machining?',
    answer: 'CNC machining is a process of cutting and shaping metal, wood and other materials with the help of computers. There are various types of CNC machines that are used for different processes such as routing, milling, engraving and turning.'
  },
  {
    question: 'What is types of CNC machine?',
    answer: 'The most common types of CNC machine include CNC Lathes and Turning Machines, CNC Milling Machines, CNC Grinding Machines, CNC Laser Cutting Machines, CNC Electrical Discharge Machines(EDM), CNC Water-jet Cutting Machine.'
  },
  {
    question: 'How cnc machining works?',
    answer: 'CNC machining is controled by a computer, it takes design from a computer software and then uses the 3-axis or 5-axis milling/turning tool to precisely cut away at the material until the desired component is produced.'
  },
  {
    question: 'How much does cnc machining cost?',
    answer: 'The price of CNC machining per hour can be as low as $10 or as high as $200 per hour depending on the type of material and cnc machine being used.'
  },
  {
    question: 'What material does cnc machining use?',
    answer: '<strong>Plastic:</strong> ABS, PC, ABS+PC, PP, PS, POM, PMMA (Acrylic), PAGF30, PCGF30, Teflon, DHPE, HDPE, PPS, PEEK.<br/><strong>Metal:</strong> Aluminum, Steel, Stainless Steel (17-4, Inconel 625 & 718), Magnesium, Titanium, Zinc, Brass, Bronze, Copper.<br/><br/>The materials above are the most common materials for CNC machining. Besides, we can work with any type of machinable plastic and metal that can be purchased from the market.'
  }
])

// 3. Sheet Metal Fabrication 数据
const sheetMetalFaqList = ref([
  {
    question: 'What is sheet metal fabrication?',
    answer: 'Sheet metal fabrication is the technique of creating metal pieces out of a single sheet of material.'
  },
  {
    question: 'What is the sheet metal fabrication process?',
    answer: 'Sheet metal fabrication involves cutting, bending and assembling processes to fabricate a sheet metal workpiece into a final product. A wide range of procedures exist which are used in processing sheet metal. As a general rule, these processes can be grouped into "cutting" and "deforming" categories.'
  }
])

// 4. 3D Printing / Additive Manufacturing 数据 (NEW)
const printingFaqList = ref([
  {
    question: 'What is 3D Printing/Additive Manufacturing?',
    answer: '3D printing or additive manufacturing is a manufacturing process that turns a digital model file into a physical object. The technology works by layering material on top of material to create a complete object.'
  }
])

// 5. Surface Finish 数据 (NEW)
const surfaceFaqList = ref([
  {
    question: 'What is Surface Finish?',
    answer: 'Surface finish or surface treatment is an additional process applied to the surface of a material for the purpose of adding functions such as rust and wear resistance or improving the decorative properties to enhance its appearance.'
  },
])
</script>

<style scoped>
/* ==========================================================================
   全屏基础容器与排版
   ========================================================================== */
.faq-container {
  background-color: #f8fafc;
  padding: 4rem 2rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  box-sizing: border-box;
  margin-top: -60px;
}

.faq-container *, .faq-container *::before, .faq-container *::after {
  box-sizing: border-box;
}

/* ==========================================================================
   头部大标题样式
   ========================================================================== */
.faq-header {
  max-width: 75rem;
  margin: 0 auto 3rem auto;
  text-align: center;
}

.faq-header h1 {
  font-size: 2.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 1rem 0;
  letter-spacing: -0.025em;
}

.faq-header .divider {
  width: 3.5rem;
  height: 2px;
  background-color: #94a3b8;
  margin: 0 auto 1.5rem auto;
}

.faq-header p {
  font-size: 0.95rem;
  color: #64748b;
  margin: 0;
}

/* ==========================================================================
   主卡片容器 & 标题
   ========================================================================== */
.faq-card {
  max-width: 75rem;
  margin: 0 auto;
}

.mb-4rem {
  margin-bottom: 4rem; /* 用于上下模块的分离间距 */
}

.section-title {
  font-size: 1.5rem;
  font-weight: 500;
  color: #1e293b;
  margin: 0 0 2rem 0;
  text-align: center;
}

/* ==========================================================================
   双列并排布局 (Flexbox)
   ========================================================================== */
.faq-columns-wrapper {
  max-width: 75rem;
  /* 替换了 margin: 0 auto; 避免覆盖 margin-bottom */
  margin-left: auto;
  margin-right: auto;
  display: flex;
  gap: 3rem;
  align-items: flex-start;
}

.faq-column {
  flex: 1;
  min-width: 28rem;
}

/* ==========================================================================
   折叠面板组件公共样式 (Accordion)
   ========================================================================== */
.accordion-group {
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  background-color: #ffffff;
  overflow: hidden;
}

.accordion-item {
  display: flex;
  flex-direction: column;
}

.accordion-item:not(:last-child) {
  border-bottom: 1px solid #e2e8f0;
}

/* 按钮触发区域 */
.accordion-trigger {
  width: 100%;
  padding: 1.1rem 1.25rem;
  display: flex;
  align-items: flex-start; /* 从 center 改为 flex-start，让内容顶部对齐 */
  gap: 0.75rem;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  user-select: none;
  transition: background-color 0.15s ease;
}

.accordion-trigger:hover {
  background-color: #f8fafc;
}

.accordion-trigger:focus {
  outline: none;
}

.question-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #334155;
  line-height: 1.4;
}

.active-trigger .question-text {
  color: #0f172a;
}

/* 带有顺时针 90° 旋转效果的 SVG 箭头 */
.arrow-icon {
  width: 0.75rem;
  height: 0.75rem;
  color: #64748b;
  flex-shrink: 0;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(0deg); /* 默认朝右 */
  margin-top: 0.25rem; /* 向下微调，使其刚好和第一行文字的中间对齐 */
}

.arrow-icon.rotate {
  transform: rotate(90deg); /* 激活朝下 */
}

/* 答案展示面板 */
.accordion-panel {
  background-color: #ffffff;
  border-top: 1px solid #f1f5f9;
}

.panel-content {
  padding: 1rem 1.25rem 1.5rem 2.25rem;
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.6;
}

/* ==========================================================================
   Banner 模块样式 (NEW)
   ========================================================================== */
.cta-module {
  max-width: 75rem;
  margin: 0 auto;
  display: flex;
  background-color: #ffffff;
  border-radius: 0.375rem;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.cta-content {
  flex: 1;
  padding: 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
}

.cta-content h2 {
  font-size: 1.75rem;
  color: #1e293b;
  margin-top: 0;
  margin-bottom: 1rem;
  font-weight: 500;
}

.cta-content p {
  color: #475569;
  line-height: 1.6;
  font-size: 0.95rem;
  margin-bottom: 2rem;
  max-width: 90%;
}

.btn-cta {
  background-color: #16bc9c; /* 匹配截图中的深蓝色 */
  color: #ffffff;
  border: none;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 0.25rem;
  cursor: pointer;
  text-transform: uppercase;
  transition: background-color 0.2s;
}

.btn-cta:hover {
  background-color: #027062;
}

.cta-image {
  flex: 1;
  display: flex;
  min-height: 100%;
}

.cta-image img {
  width: 100%;
  height: auto;
  object-fit: cover;
  display: block;
}

/* ==========================================================================
   响应式移动端断点适配
   ========================================================================== */
@media (max-width: 60rem) {
  .faq-columns-wrapper {
    flex-direction: column;
    gap: 2.5rem;
  }
  .faq-column {
    width: 100%;
    min-width: 100%;
  }
  .cta-module {
    flex-direction: column;
  }
  .cta-content {
    padding: 2rem;
    align-items: center; /* 让按钮及内容居中 */
    text-align: center;  /* 文本对齐方式改为居中 */
  }
  .cta-content p {
    max-width: 100%;
  }
}
</style>